import classNames from 'classnames/bind'
import { noop } from 'lodash'
import PropTypes from 'prop-types'
import React, { useState } from 'react'
import {
  Button,
  FixedMessage,
  Form,
  InputField,
  PasswordField,
  Text,
} from '@adc/ui-core'
import { isRequired } from '../../../util/authFormValidators'
import UserService from '../../../shared/services/user'
import cls from './styles.scss'
const cx = classNames.bind(cls)

const VerificationBox = ({
  elmId = 'auth',
  email,
  forgotPasswordLink = '/user/forgot-password',
  loginEndPoint = '/apps/api/login',
  maxRetryCount = 3,
  onClose,
  onFail = noop,
  onSuccess,
  titleMessage = 'Your session has expired. Please re-enter your password.',
}) => {
  const [password, setPassword] = useState('')
  const [hasAttemptedSubmit, setHasAttemptedSubmit] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [errorMessage, setErrorMessage] = useState(null)
  const [numberOfFailedAttempts, setNumberOfFailedAttempts] = useState(0)

  const onPasswordChange = event => {
    setPassword(event.target.value)
  }
  const passwordError = isRequired(password)

  const submitLogin = async event => {
    event.preventDefault()
    setHasAttemptedSubmit(true)
    if (passwordError) return
    if (numberOfFailedAttempts < maxRetryCount) {
      setIsSubmitting(true)
      setErrorMessage(null)
      try {
        const authResponse = await UserService.login(
          email,
          password,
          loginEndPoint
        )
        if (!authResponse.body.login) throw authResponse
        onSuccess(authResponse.body.user)
        setNumberOfFailedAttempts(0)
      } catch (error) {
        setErrorMessage(error.body?.error || 'An unknown error occurred.')
        setNumberOfFailedAttempts(prevCount => prevCount + 1)
      }
      setIsSubmitting(false)
    } else {
      onFail({ message: 'Max retries reached.' })
      setNumberOfFailedAttempts(0)
    }
  }

  return (
    <section>
      <div>
        <Text.Header className={cx('text-align-center')} color='gray' level='1'>
          Log In
        </Text.Header>
        <FixedMessage role='information' showIcon>
          {titleMessage}
        </FixedMessage>
        {errorMessage && (
          <FixedMessage role='error' showIcon>
            {errorMessage}
          </FixedMessage>
        )}
        <Form
          className='u-mt-4'
          data-elm-id={`${elmId}_form`}
          onSubmit={submitLogin}
        >
          <InputField
            disabled
            elmId={`${elmId}_email`}
            hideLabel
            type='email'
            value={email}
          />
          <PasswordField
            autoFocus
            elmId={`${elmId}_password`}
            error={passwordError}
            hideLabel
            onChange={onPasswordChange}
            placeholder='Password'
            touched={hasAttemptedSubmit}
            type='password'
            value={password}
          />
          <div className={cx('text-align-center', 'u-mt-5', 'u-mb-2')}>
            <a
              data-elm-id={`${elmId}_forgot_password`}
              href={forgotPasswordLink}
              rel='noreferrer'
              target='_blank'
            >
              Forgot password?
            </a>
          </div>
          <Button
            disabled={isSubmitting}
            elmId={`${elmId}_submit_button`}
            fluid
            loading={isSubmitting}
            role='primary'
            type='submit'
          >
            {isSubmitting ? 'Logging In...' : 'Log In'}
          </Button>
        </Form>
        <div className={cx('text-align-center', 'u-mt-4')}>
          <Text
            as='span'
            className='u-pointer-cursor'
            color='info'
            data-elm-id={`${elmId}_not_now`}
            onClick={onClose}
            size='tiny'
          >
            Not Now
          </Text>
        </div>
      </div>
    </section>
  )
}

VerificationBox.propTypes = {
  elmId: PropTypes.string,
  email: PropTypes.string.isRequired,
  forgotPasswordLink: PropTypes.string,
  loginEndPoint: PropTypes.string,
  maxRetryCount: PropTypes.number,
  onClose: PropTypes.func.isRequired,
  onFail: PropTypes.func,
  onSuccess: PropTypes.func.isRequired,
  titleMessage: PropTypes.string,
}

export default VerificationBox
